import { useApolloClient } from '@apollo/client';
import {
	ArcElement,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	Legend,
	LinearScale,
	Plugin,
	Title,
	Tooltip,
} from 'chart.js';
import moment from 'moment';
import * as Papa from 'papaparse';
import { rgba } from 'polished';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import { useAnalyticsInterval } from '@treams/treams-hooks/src/useAnalyticsInterval';
import {
	PromptHistoryAnalyticsQuery,
	PromptHistoryExportDocument,
	PromptHistoryExportQuery,
	PromptHistoryExportQueryVariables,
	usePromptHistoryAnalyticsQuery,
	useWorkspacesMinimalQuery,
} from '@treams/treams-network';
import {
	Button,
	DateIntervalNavigation,
	FlexGap,
	Heading3,
	ITimeMachine,
	Paragraph,
	Select,
	Spinner,
	Switch,
	Table,
	createTimeMachine,
	tokens,
} from '@treams/treams-ui';

import {
	rankWorkspacesByCost,
	summarizePromptUsageCosts,
} from '../../ai-agent/estimateAgentChatCost';
import { targetTypeMapping } from './ChatHistory';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const CHART_FONT = { family: 'Source Sans Pro', size: 12 };

interface IGaugeNeedleOptions {
	value: number;
	color: string;
}

const gaugeNeedle: Plugin<'doughnut', IGaugeNeedleOptions> = {
	id: 'gaugeNeedle',
	afterDatasetDraw(chart, _args, options) {
		const value = options?.value;
		if (value == null) return;

		const meta = chart.getDatasetMeta(0);
		const arc = meta.data[0] as unknown as { x: number; y: number; outerRadius: number };
		if (!arc) return;

		const { ctx } = chart;
		const cx = arc.x;
		const cy = arc.y;
		const clamped = Math.max(0, Math.min(100, value));
		const angle = Math.PI * (1 - clamped / 100);
		const needleLength = arc.outerRadius * 0.82;

		ctx.save();
		ctx.translate(cx, cy);
		ctx.rotate(-angle);
		ctx.beginPath();
		ctx.moveTo(0, -3);
		ctx.lineTo(needleLength, 0);
		ctx.lineTo(0, 3);
		ctx.fillStyle = options?.color ?? '#333';
		ctx.fill();
		ctx.restore();

		ctx.beginPath();
		ctx.arc(cx, cy, 6, 0, Math.PI * 2);
		ctx.fillStyle = options?.color ?? '#333';
		ctx.fill();
	},
};

const ChartCard = styled(Box)`
	background: ${(p) => p.theme.colors.white};
	border: 1px solid ${(p) => p.theme.colors.lightBlueLight};
	border-radius: 3px;
	padding: 16px;
	margin-bottom: 16px;
`;

const ChartTitle = styled(Paragraph)`
	font-weight: 600;
	margin-bottom: 8px;
`;

const ChartWrap = styled(Box)`
	position: relative;
	height: 280px;
`;

const TallChartWrap = styled(Box)`
	position: relative;
	height: 320px;
`;

const GaugeWrap = styled(Box)`
	position: relative;
	text-align: center;
`;

const GaugeChart = styled(Box)`
	position: relative;
	height: 200px;
`;

const GaugeValue = styled(Paragraph)`
	font-size: 40px;
	font-weight: 700;
	line-height: 1;
	margin-top: -56px;
`;

const GaugeCaption = styled(Paragraph)`
	margin-top: 4px;
	margin-bottom: 4px;
`;

const LoadingPanel = styled(Flex)`
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 360px;
	padding: 32px 16px;
	background: ${(p) => p.theme.colors.white};
	border: 1px solid ${(p) => p.theme.colors.lightBlueLight};
	border-radius: 3px;
`;

const SpinnerScale = styled(Box)`
	transform: scale(2);
	transform-origin: center;
	margin-bottom: 24px;
`;

function formatUsd(value: number): string {
	const digits = Math.abs(value) > 0 && Math.abs(value) < 0.01 ? 4 : 2;
	return value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: digits,
	});
}

function weekLabel(key: string): string {
	const m = moment(key, 'GGGG-[W]WW');
	return m.isValid() ? m.format('LL') : key;
}

function labelForTargetType(tt: string): string {
	return targetTypeMapping[tt] ?? tt;
}

const EXPORT_TARGET_TYPE_GROUPS: Record<string, string> = {
	CONVERSATION_INSIGHTS_EMPLOYEE: 'CONVERSATION_EVALUATION',
	CONVERSATION_INSIGHTS_MANAGER: 'CONVERSATION_EVALUATION',
	EVALUATION_SUBMISSION: 'CONVERSATION_EVALUATION',
};

function groupedTargetType(topic: string): string {
	return EXPORT_TARGET_TYPE_GROUPS[topic] ?? topic;
}

function providerFromModel(model: string | null | undefined): string {
	if (!model) return '';
	const m = model.toLowerCase();
	if (m.startsWith('gpt') || m.startsWith('o1') || m.startsWith('o3')) return 'OpenAI';
	if (m.startsWith('claude')) return 'Anthropic';
	if (m.startsWith('gemini')) return 'Google';
	return '';
}

type AnalyticsPayload = PromptHistoryAnalyticsQuery['promptHistoryAnalytics'];

function resolveTimeSeries(
	analytics: AnalyticsPayload,
	interval: ITimeMachine['interval'],
): {
	kind: 'day' | 'week' | 'month';
	keys: string[];
	getCounts: (key: string) => { targetType: string; count: number }[] | undefined;
	label: (key: string) => string;
} {
	if (interval === 'year' && analytics.byMonth.length > 0) {
		return {
			kind: 'month',
			keys: [...analytics.byMonth].map((m) => m.monthKey).sort((a, b) => a.localeCompare(b)),
			getCounts: (key) => analytics.byMonth.find((m) => m.monthKey === key)?.countsByTargetType,
			label: (key) => moment(`${key}-01`, 'YYYY-MM-DD').format('MMM YYYY'),
		};
	}
	if (
		analytics.byDay.length > 0 &&
		(interval === 'day' || interval === 'week' || interval === 'month' || interval === 'custom')
	) {
		return {
			kind: 'day',
			keys: [...analytics.byDay].map((d) => d.dayKey).sort((a, b) => a.localeCompare(b)),
			getCounts: (key) => analytics.byDay.find((d) => d.dayKey === key)?.countsByTargetType,
			label: (key) => moment(key, 'YYYY-MM-DD').format('ddd D MMM'),
		};
	}
	return {
		kind: 'week',
		keys: [...analytics.byWeek].map((w) => w.weekKey).sort((a, b) => a.localeCompare(b)),
		getCounts: (key) => analytics.byWeek.find((w) => w.weekKey === key)?.countsByTargetType,
		label: weekLabel,
	};
}

function buildChartModel(analytics: AnalyticsPayload, interval: ITimeMachine['interval']) {
	const byType: Record<string, number> = Object.fromEntries(
		analytics.byTargetType.map((x) => [x.targetType, x.count]),
	);
	const typeKeys = [...analytics.byTargetType]
		.sort((a, b) => b.count - a.count)
		.map((x) => x.targetType);

	const typeSet = new Set(typeKeys);
	for (const w of analytics.byWeek) {
		for (const c of w.countsByTargetType) {
			typeSet.add(c.targetType);
		}
	}
	for (const d of analytics.byDay) {
		for (const c of d.countsByTargetType) {
			typeSet.add(c.targetType);
		}
	}
	for (const m of analytics.byMonth) {
		for (const c of m.countsByTargetType) {
			typeSet.add(c.targetType);
		}
	}
	const seriesTypes = [...typeSet].sort((a, b) => (byType[b] ?? 0) - (byType[a] ?? 0));

	const timeSeries = resolveTimeSeries(analytics, interval);
	const byPeriodByType: Record<string, Record<string, number>> = {};
	for (const key of timeSeries.keys) {
		const counts = timeSeries.getCounts(key);
		byPeriodByType[key] = counts
			? Object.fromEntries(counts.map((c) => [c.targetType, c.count]))
			: {};
	}

	const ratingByType: Record<string, { up: number; down: number }> = {};
	for (const f of analytics.feedbackByTargetType) {
		ratingByType[f.targetType] = { up: f.positiveCount, down: f.negativeCount };
	}

	const minDate = analytics.dateRange?.min ? moment(analytics.dateRange.min) : null;
	const maxDate = analytics.dateRange?.max ? moment(analytics.dateRange.max) : null;

	return {
		byType,
		byPeriodByType,
		periodKeys: timeSeries.keys,
		periodLabel: timeSeries.label,
		timeSeriesKind: timeSeries.kind,
		typeKeys,
		seriesTypes,
		evalEmployee: analytics.evaluationContext.asEmployeeCount,
		evalManager: analytics.evaluationContext.asManagerCount,
		evalUnknownRole: analytics.evaluationContext.unknownCount,
		ratingByType,
		uniqueUsers: analytics.uniqueUserCount,
		uniqueWorkspaces: analytics.uniqueWorkspaceCount,
		totalInteractions: analytics.totalInteractions,
		minDate,
		maxDate,
		feedbackTotals: analytics.feedbackTotals,
		topWorkspaces: analytics.topWorkspaces,
	};
}

const TOP_WORKSPACES_CHART = 10;

export default function ChatHistoryAnalytics() {
	const { t } = useTranslation();
	const theme = useTheme();
	const configuration = useWorkspaceConfiguration();
	const { setInterval, setStartDate, setEndDate } = useAnalyticsInterval();
	const [showDemo, setShowDemo] = useState(false);
	const [targetType, setTargetType] = useState<string | undefined>(undefined);
	const [workspaceIDFilter, setWorkspaceIDFilter] = useState<string | undefined>(undefined);
	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(() =>
		createTimeMachine('week', moment().toDate()),
	);
	const [ratingsExpanded, setRatingsExpanded] = useState(false);
	const [onlyWrittenFeedback, setOnlyWrittenFeedback] = useState(false);
	const [exporting, setExporting] = useState(false);
	const apolloClient = useApolloClient();

	useEffect(() => {
		setInterval(timeMachine.interval);
		setStartDate(timeMachine.current[0]);
		setEndDate(timeMachine.current[1]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeMachine]);

	useEffect(() => {
		setRatingsExpanded(false);
	}, [workspaceIDFilter, targetType, showDemo, timeMachine]);

	const calendarTimeZone = useMemo(
		() => Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
		[],
	);

	const workspacesQuery = useWorkspacesMinimalQuery({
		variables: {
			filters: { isArchived: false },
			first: 1000,
		},
	});

	const workspaceFilterOptions = useMemo(() => {
		const edges = workspacesQuery.data?.workspacesV2.edges ?? [];
		return edges
			.map(({ node }) => ({
				title: node.alias ? `${node.alias} (${node.id})` : node.id,
				value: node.id,
			}))
			.filter((o) => o.value !== 'launch')
			.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
	}, [workspacesQuery.data]);

	const workspaceTitleById = useMemo(() => {
		const m = new Map<string, string>();
		for (const { node } of workspacesQuery.data?.workspacesV2.edges ?? []) {
			m.set(node.id, node.alias ? `${node.alias} (${node.id})` : node.id);
		}
		return m;
	}, [workspacesQuery.data]);

	const query = usePromptHistoryAnalyticsQuery({
		variables: {
			filters: {
				filter: workspaceIDFilter,
				includeDemoWorkspaces: showDemo,
				targetType: targetType ?? undefined,
			},
			startDate: timeMachine.current[0].toISOString(),
			endDate: timeMachine.current[1].toISOString(),
			calendarTimeZone,
		},
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const analytics = query.data?.promptHistoryAnalytics;

	const visibleFeedbackDetails = useMemo(() => {
		const rows = analytics?.feedbackDetails ?? [];
		if (!onlyWrittenFeedback) return rows;
		return rows.filter((row) => Boolean(row.writtenFeedback?.trim()));
	}, [analytics?.feedbackDetails, onlyWrittenFeedback]);

	const stats = useMemo(
		() => (analytics ? buildChartModel(analytics, timeMachine.interval) : null),
		[analytics, timeMachine.interval],
	);

	const costs = useMemo(
		() => (analytics ? summarizePromptUsageCosts(analytics.usageByModel) : null),
		[analytics],
	);

	const workspaceCosts = useMemo(
		() => (analytics ? rankWorkspacesByCost(analytics.usageByWorkspace) : []),
		[analytics],
	);

	const handleExport = useCallback(async () => {
		setExporting(true);
		try {
			const response = await apolloClient.query<
				PromptHistoryExportQuery,
				PromptHistoryExportQueryVariables
			>({
				query: PromptHistoryExportDocument,
				variables: {
					filters: {
						filter: workspaceIDFilter,
						includeDemoWorkspaces: showDemo,
						targetType: targetType ?? undefined,
					},
					startDate: timeMachine.current[0].toISOString(),
					endDate: timeMachine.current[1].toISOString(),
				},
				fetchPolicy: 'network-only',
			});

			const rows = [...(response.data?.promptHistoryExport ?? [])].sort(
				(a, b) => moment.utc(a.createdAt).valueOf() - moment.utc(b.createdAt).valueOf(),
			);

			const records = rows.map((row) => {
				const created = moment.utc(row.createdAt);
				const total =
					row.inputTokens != null || row.outputTokens != null
						? (row.inputTokens ?? 0) + (row.outputTokens ?? 0)
						: null;
				return [
					row.id,
					created.format('YYYY-MM-DD HH:mm:ss'),
					created.format('YYYY-MM'),
					row.workspaceID,
					row.userID,
					row.targetType,
					groupedTargetType(row.targetType),
					row.model ?? '',
					providerFromModel(row.model),
					row.inputTokens ?? '',
					row.outputTokens ?? '',
					total ?? '',
				];
			});

			const csv = Papa.unparse([
				[
					'id',
					'datum',
					'maand',
					'workspace',
					'gebruiker',
					'onderwerp',
					'target_type',
					'model',
					'provider',
					'prompt_tokens',
					'completion_tokens',
					'total_tokens',
				],
				...records,
			]);

			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `ai_usage_per_prompt_${moment().format('YYYY-MM-DD')}.csv`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch {
			// Swallow the error so it doesn't propagate to the Button's click
			// handler, which would otherwise leave it stuck in a loading state.
		} finally {
			setExporting(false);
		}
	}, [apolloClient, workspaceIDFilter, showDemo, targetType, timeMachine]);

	const doughnutData = useMemo(() => {
		if (!stats) {
			return {
				labels: [],
				datasets: [{ data: [], backgroundColor: [], borderColor: [], borderWidth: 1 }],
			};
		}
		const labels = stats.typeKeys.map((k) => labelForTargetType(k));
		const data = stats.typeKeys.map((k) => stats.byType[k] ?? 0);
		const colors = [
			theme.colors.primary,
			theme.colors.green,
			theme.colors.yellow,
			tokens.text.secondary,
			theme.colors.darkGray,
		];
		return {
			labels,
			datasets: [
				{
					data,
					backgroundColor: stats.typeKeys.map((_, i) => rgba(colors[i % colors.length], 0.75)),
					borderColor: stats.typeKeys.map((_, i) => colors[i % colors.length]),
					borderWidth: 1,
				},
			],
		};
	}, [stats, theme.colors]);

	const stackedBarData = useMemo(() => {
		if (!stats) {
			return { labels: [], datasets: [] };
		}
		const colors = [
			theme.colors.primary,
			theme.colors.green,
			theme.colors.yellow,
			tokens.text.secondary,
		];
		return {
			labels: stats.periodKeys.map((k) => stats.periodLabel(k)),
			datasets: stats.seriesTypes.map((tt, i) => ({
				label: labelForTargetType(tt),
				data: stats.periodKeys.map((pk) => stats.byPeriodByType[pk]?.[tt] ?? 0),
				backgroundColor: rgba(colors[i % colors.length], 0.8),
				borderColor: colors[i % colors.length],
				borderWidth: 1,
				stack: 'usage',
			})),
		};
	}, [stats, theme.colors]);

	const evalRoleData = useMemo(() => {
		if (!stats) return { labels: [], datasets: [] };
		const labels = [
			t('settings.ai-workbench.chat-history.employee', { defaultValue: 'Employee' }),
			t('settings.ai-workbench.chat-history.manager', { defaultValue: 'Manager' }),
			t('settings.ai-workbench.analytics.eval-unknown-role', {
				defaultValue: 'Role unknown',
			}),
		];
		return {
			labels,
			datasets: [
				{
					label: labelForTargetType('EVALUATION_SUBMISSION'),
					data: [stats.evalEmployee, stats.evalManager, stats.evalUnknownRole],
					backgroundColor: rgba(theme.colors.primary, 0.75),
					borderColor: theme.colors.primary,
					borderWidth: 1,
				},
			],
		};
	}, [stats, t, theme.colors.primary]);

	const ratingBarData = useMemo(() => {
		if (!stats) return { labels: [], datasets: [] };
		const types = Object.keys(stats.ratingByType)
			.filter((tt) => {
				const { up, down } = stats.ratingByType[tt];
				return up + down > 0;
			})
			.sort();
		const positivePct = types.map((tt) => {
			const { up, down } = stats.ratingByType[tt];
			const total = up + down;
			return total ? Math.round((100 * up) / total) : 0;
		});
		return {
			labels: types.map(labelForTargetType),
			datasets: [
				{
					label: t('settings.ai-workbench.analytics.rating-positive-percent', {
						defaultValue: 'Positive ratings',
					}),
					data: positivePct,
					backgroundColor: rgba(theme.colors.green, 0.65),
					borderColor: theme.colors.green,
					borderWidth: 1,
				},
			],
		};
	}, [stats, t, theme.colors.green]);

	const ratingGauge = useMemo(() => {
		if (!stats) return null;
		let up = 0;
		let down = 0;
		for (const tt of Object.keys(stats.ratingByType)) {
			up += stats.ratingByType[tt].up;
			down += stats.ratingByType[tt].down;
		}
		const total = up + down;
		if (total === 0) return null;
		return { pct: Math.round((100 * up) / total), up, down, total };
	}, [stats]);

	const gaugeColor = useMemo(() => {
		if (!ratingGauge) return tokens.text.secondary;
		if (ratingGauge.pct >= 80) return theme.colors.green;
		if (ratingGauge.pct >= 50) return theme.colors.yellow;
		return theme.colors.danger;
	}, [ratingGauge, theme.colors]);

	const gaugeData = useMemo(
		() => ({
			labels: [],
			datasets: [
				{
					data: [50, 30, 20],
					backgroundColor: [
						rgba(theme.colors.danger, 0.75),
						rgba(theme.colors.yellow, 0.75),
						rgba(theme.colors.green, 0.75),
					],
					borderColor: theme.colors.white,
					borderWidth: 2,
					circumference: 180,
					rotation: 270,
				},
			],
		}),
		[theme.colors],
	);

	const gaugeOptions: ChartOptions<'doughnut'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			cutout: '70%',
			circumference: 180,
			rotation: 270,
			plugins: {
				legend: { display: false },
				tooltip: { enabled: false },
				gaugeNeedle: { value: ratingGauge?.pct ?? 0, color: tokens.text.secondary },
			},
		}),
		[ratingGauge],
	);

	const workspaceCostBarData = useMemo(() => {
		if (!workspaceCosts.length) return { labels: [], datasets: [] };
		return {
			labels: workspaceCosts.map(
				(workspace) => workspaceTitleById.get(workspace.workspaceID) ?? workspace.workspaceID,
			),
			datasets: [
				{
					label: t('settings.ai-workbench.analytics.costs-estimated'),
					data: workspaceCosts.map((workspace) => workspace.estimatedCostUsd),
					backgroundColor: rgba(tokens.info.icon, 0.7),
					borderColor: tokens.info.icon,
					borderWidth: 1,
				},
			],
		};
	}, [workspaceCosts, workspaceTitleById, t]);

	const workspaceBarData = useMemo(() => {
		if (!stats?.topWorkspaces?.length) return { labels: [], datasets: [] };

		const slice = stats.topWorkspaces.slice(0, TOP_WORKSPACES_CHART);
		return {
			labels: slice.map((w) => w.workspaceID),
			datasets: [
				{
					label: t('settings.ai-workbench.analytics.workspace-interactions', {
						defaultValue: 'Interactions',
					}),
					data: slice.map((w) => w.interactionCount),
					backgroundColor: rgba(theme.colors.primary, 0.7),
					borderColor: theme.colors.primary,
					borderWidth: 1,
				},
			],
		};
	}, [stats, t, theme.colors.primary]);

	const legendPlugin = useMemo(
		() => ({
			position: 'bottom' as const,
			labels: { color: tokens.text.secondary, font: CHART_FONT },
		}),
		[],
	);

	const doughnutOptions: ChartOptions<'doughnut'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: legendPlugin },
		}),
		[legendPlugin],
	);

	const barOptions: ChartOptions<'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: legendPlugin },
			scales: {
				x: {
					stacked: true,
					ticks: { color: tokens.text.secondary, font: CHART_FONT },
					grid: { color: tokens.surface.secondary },
				},
				y: {
					stacked: true,
					beginAtZero: true,
					ticks: { precision: 0, color: tokens.text.secondary, font: CHART_FONT },
					grid: { color: tokens.surface.secondary },
				},
			},
		}),
		[legendPlugin],
	);

	const ratingBarOptions: ChartOptions<'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: legendPlugin },
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					max: 100,
					ticks: {
						color: tokens.text.secondary,
						font: CHART_FONT,
						callback: (v) => `${v}%`,
					},
					grid: { color: tokens.surface.secondary },
				},
				y: {
					ticks: { color: tokens.text.secondary, font: CHART_FONT },
					grid: { display: false },
				},
			},
		}),
		[legendPlugin],
	);

	const horizontalEvalOptions: ChartOptions<'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: legendPlugin },
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					ticks: { precision: 0, color: tokens.text.secondary, font: CHART_FONT },
					grid: { color: tokens.surface.secondary },
				},
				y: {
					ticks: { color: tokens.text.secondary, font: CHART_FONT },
					grid: { display: false },
				},
			},
		}),
		[legendPlugin],
	);

	const workspaceBarOptions: ChartOptions<'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: legendPlugin },
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					ticks: { precision: 0, color: tokens.text.secondary, font: CHART_FONT },
					grid: { color: tokens.surface.secondary },
				},
				y: {
					ticks: { color: tokens.text.secondary, font: CHART_FONT, maxRotation: 0 },
					grid: { display: false },
				},
			},
		}),
		[legendPlugin],
	);

	const workspaceCostBarOptions: ChartOptions<'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: legendPlugin,
				tooltip: {
					callbacks: {
						label: (item) => formatUsd(Number(item.raw)),
					},
				},
			},
			indexAxis: 'y' as const,
			scales: {
				x: {
					beginAtZero: true,
					ticks: {
						color: tokens.text.secondary,
						font: CHART_FONT,
						callback: (value) => formatUsd(Number(value)),
					},
					grid: { color: tokens.surface.secondary },
				},
				y: {
					ticks: { color: tokens.text.secondary, font: CHART_FONT, maxRotation: 0 },
					grid: { display: false },
				},
			},
		}),
		[legendPlugin],
	);

	const showEvalRoleChart =
		stats != null && stats.evalEmployee + stats.evalManager + stats.evalUnknownRole > 0;
	const showRatingChart =
		stats != null &&
		Object.keys(stats.ratingByType).some((tt) => {
			const { up, down } = stats.ratingByType[tt];
			return up + down > 0;
		});
	const showWorkspaceChart =
		stats != null && stats.topWorkspaces.length > 0 && workspaceIDFilter == null;
	const showWorkspaceCostChart = workspaceCosts.length > 0 && workspaceIDFilter == null;

	if (query.error) {
		return (
			<Box>
				<Paragraph color="danger">
					{t('settings.ai-workbench.analytics.load-error', {
						defaultValue: 'Could not load chat history for analytics.',
					})}
				</Paragraph>
			</Box>
		);
	}

	return (
		<Box display="flex" flexDirection="column" flexGrow={1}>
			<Paragraph mb={3} color="slateBlue">
				{t('settings.ai-workbench.analytics.disclaimer', {
					defaultValue:
						'Aggregated metrics from the server (no chat message content). Optionally pick one workspace, then use the calendar and subject filter.',
				})}
			</Paragraph>
			<Box flexGrow={1} flexBasis={0} display="flex" alignItems="center" mb={2} flexWrap="wrap">
				<FlexGap gap={8} alignItems="center" mt={2} flexWrap="wrap">
					<Box minWidth={260}>
						<Select
							search
							small
							disabled={workspacesQuery.loading}
							selectedValue={workspaceIDFilter}
							options={[
								{
									title: t('settings.ai-workbench.analytics.all-workspaces', {
										defaultValue: 'All workspaces',
									}),
									value: undefined,
								},
								...workspaceFilterOptions,
							]}
							onChange={(v) => setWorkspaceIDFilter(v as string | undefined)}
						/>
					</Box>
					<Box minWidth={200}>
						<Select
							search={false}
							small
							selectedValue={targetType}
							options={[
								{
									title: t('settings.ai-workbench.analytics.all-subjects', {
										defaultValue: 'All subjects',
									}),
									value: undefined,
								},
								{
									title: targetTypeMapping.CONVERSATION_INSIGHTS_EMPLOYEE,
									value: 'CONVERSATION_INSIGHTS_EMPLOYEE',
								},
								{
									title: targetTypeMapping.CONVERSATION_INSIGHTS_MANAGER,
									value: 'CONVERSATION_INSIGHTS_MANAGER',
								},
								{ title: targetTypeMapping.TRANSCRIPTION_SUMMARY, value: 'TRANSCRIPTION_SUMMARY' },
								{ title: targetTypeMapping.NOTE_SUMMARIES, value: 'NOTE_SUMMARIES' },
								{ title: targetTypeMapping.TALKING_POINTS, value: 'TALKING_POINTS' },
								{ title: targetTypeMapping.TALENT_INSIGHTS, value: 'TALENT_INSIGHTS' },
								{
									title: targetTypeMapping.TREAMS_SCORE_INSIGHTS,
									value: 'TREAMS_SCORE_INSIGHTS',
								},
								{ title: targetTypeMapping.AI_AGENT, value: 'AI_AGENT' },
							]}
							onChange={setTargetType}
						/>
					</Box>
					<Box minWidth={280}>
						<DateIntervalNavigation
							disableQuery
							allowFutureDates={false}
							value={timeMachine}
							onChange={setTimeMachine}
							workspaceStartDate={
								configuration.data?.startDate
									? moment(configuration.data.startDate).toDate()
									: undefined
							}
						/>
					</Box>
					<Box minWidth={250}>
						<Switch
							label={t('settings.ai-workbench.include-demo-workspaces')}
							on={showDemo}
							onChange={setShowDemo}
						/>
					</Box>
					<Box>
						<Button
							small
							type="secondary"
							loading={exporting}
							disabled={exporting || query.loading || !stats || stats.totalInteractions === 0}
							onClick={handleExport}
							title={t('settings.ai-workbench.analytics.export-csv', {
								defaultValue: 'Export CSV',
							})}
						/>
					</Box>
				</FlexGap>
			</Box>

			{query.loading && !analytics ? (
				<LoadingPanel>
					<SpinnerScale>
						<Spinner color="primary" />
					</SpinnerScale>
					<Paragraph textAlign="center" mb={2} style={{ fontWeight: 600 }}>
						{t('settings.ai-workbench.analytics.loading', {
							defaultValue: 'Loading analytics…',
						})}
					</Paragraph>
				</LoadingPanel>
			) : !stats || stats.totalInteractions === 0 ? (
				<Paragraph>
					{t('settings.ai-workbench.analytics.empty', {
						defaultValue: 'No chat history matches the current filters.',
					})}
				</Paragraph>
			) : (
				<>
					<Flex flexWrap="wrap" mx={-2} mb={3}>
						<Box width={[1, 1 / 2, 1 / 4]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.summary-total', {
										defaultValue: 'Total AI interactions',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 28, fontWeight: 700 }}>
									{stats.totalInteractions}
								</Paragraph>
							</ChartCard>
						</Box>
						<Box width={[1, 1 / 2, 1 / 4]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.summary-users', {
										defaultValue: 'Distinct users',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 28, fontWeight: 700 }}>{stats.uniqueUsers}</Paragraph>
							</ChartCard>
						</Box>
						<Box width={[1, 1 / 2, 1 / 4]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.summary-workspaces', {
										defaultValue: 'Unique workspaces',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 28, fontWeight: 700 }}>
									{stats.uniqueWorkspaces}
								</Paragraph>
							</ChartCard>
						</Box>
						<Box width={[1, 1 / 2, 1 / 4]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.summary-range', {
										defaultValue: 'Activity date range',
									})}
								</ChartTitle>
								<Paragraph>
									{stats.minDate && stats.maxDate
										? `${stats.minDate.format('L')} — ${stats.maxDate.format('L')}`
										: '—'}
								</Paragraph>
							</ChartCard>
						</Box>
					</Flex>

					<Flex flexWrap="wrap" mx={-2} mb={3}>
						<Box width={[1, 1 / 3]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.feedback-no-rating', {
										defaultValue: 'No rating',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 24, fontWeight: 700 }}>
									{stats.feedbackTotals.noRatingCount}
								</Paragraph>
							</ChartCard>
						</Box>
						<Box width={[1, 1 / 3]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.feedback-with-text', {
										defaultValue: 'Rating with written feedback',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 24, fontWeight: 700 }}>
									{stats.feedbackTotals.withWrittenFeedbackCount}
								</Paragraph>
							</ChartCard>
						</Box>
						<Box width={[1, 1 / 3]} px={2} mb={3}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.feedback-thumbs-only', {
										defaultValue: 'Rating without written feedback',
									})}
								</ChartTitle>
								<Paragraph style={{ fontSize: 24, fontWeight: 700 }}>
									{stats.feedbackTotals.ratingWithoutWrittenFeedbackCount}
								</Paragraph>
							</ChartCard>
						</Box>
					</Flex>

					{(ratingGauge || showRatingChart) && (
						<Flex flexWrap="wrap" mx={-2}>
							{ratingGauge && (
								<Box width={[1, 1, 1 / 2]} px={2}>
									<ChartCard>
										<ChartTitle>
											{t('settings.ai-workbench.analytics.chart-ratings-gauge', {
												defaultValue: 'Overall rating performance',
											})}
										</ChartTitle>
										<GaugeWrap>
											<GaugeChart>
												<Doughnut data={gaugeData} options={gaugeOptions} plugins={[gaugeNeedle]} />
											</GaugeChart>
											<GaugeValue style={{ color: gaugeColor }}>{ratingGauge.pct}%</GaugeValue>
											<GaugeCaption color="slateBlue">
												{t('settings.ai-workbench.analytics.chart-ratings-gauge-caption', {
													defaultValue: '{{up}} of {{total}} ratings positive',
													up: ratingGauge.up,
													total: ratingGauge.total,
												})}
											</GaugeCaption>
										</GaugeWrap>
									</ChartCard>
								</Box>
							)}
							{showRatingChart && (
								<Box width={[1, 1, 1 / 2]} px={2}>
									<ChartCard>
										<ChartTitle>
											{t('settings.ai-workbench.analytics.chart-ratings', {
												defaultValue: 'Positive rating share (%) by subject',
											})}
										</ChartTitle>
										<ChartWrap>
											<Bar data={ratingBarData} options={ratingBarOptions} />
										</ChartWrap>
									</ChartCard>
								</Box>
							)}
						</Flex>
					)}

					{analytics && analytics.feedbackDetails.length > 0 && (
						<Box mx={-2} mb={3}>
							<Box px={2}>
								<ChartCard>
									<Flex
										alignItems="center"
										justifyContent="space-between"
										flexWrap="wrap"
										style={{ gap: 8 }}
										mb={2}
									>
										<ChartTitle style={{ marginBottom: 0 }}>
											{t('settings.ai-workbench.analytics.ratings-detail-title', {
												defaultValue: 'Individual ratings',
											})}
										</ChartTitle>
										<FlexGap alignItems="center" gap={tokens.gap.group}>
											{ratingsExpanded && (
												<Switch
													on={onlyWrittenFeedback}
													onChange={setOnlyWrittenFeedback}
													label={t('settings.ai-workbench.analytics.ratings-only-written-feedback')}
												/>
											)}
											<Button
												small
												type="secondary"
												title={
													ratingsExpanded
														? t('settings.ai-workbench.analytics.ratings-detail-collapse', {
																defaultValue: 'Hide list',
															})
														: t('settings.ai-workbench.analytics.ratings-detail-expand', {
																defaultValue: 'Show all ratings',
															})
												}
												onClick={() => setRatingsExpanded((v) => !v)}
											/>
										</FlexGap>
									</Flex>
									{ratingsExpanded && (
										<>
											{analytics.feedbackDetailsTruncated && (
												<Paragraph color="slateBlue" mb={2} small>
													{t('settings.ai-workbench.analytics.ratings-detail-truncated', {
														defaultValue:
															'Showing the 500 most recent ratings for this filter. Narrow the date range or workspace to see fewer results.',
													})}
												</Paragraph>
											)}
											{visibleFeedbackDetails.length === 0 ? (
												<Paragraph color="slateBlue" small>
													{t('settings.ai-workbench.analytics.ratings-only-written-feedback-empty')}
												</Paragraph>
											) : (
												<Box style={{ overflowX: 'auto' }}>
													<Table>
														<Table.Header>
															<Table.Cell basis={0.14}>
																{t('settings.ai-workbench.analytics.ratings-col-date', {
																	defaultValue: 'Date',
																})}
															</Table.Cell>
															<Table.Cell basis={0.18}>
																{t('settings.ai-workbench.analytics.ratings-col-workspace', {
																	defaultValue: 'Workspace',
																})}
															</Table.Cell>
															<Table.Cell basis={0.14}>
																{t('settings.ai-workbench.analytics.ratings-col-subject', {
																	defaultValue: 'Subject',
																})}
															</Table.Cell>
															<Table.Cell basis={0.14}>
																{t('settings.ai-workbench.analytics.ratings-col-user', {
																	defaultValue: 'User',
																})}
															</Table.Cell>
															<Table.Cell basis={0.1}>
																{t('settings.ai-workbench.analytics.ratings-col-rating', {
																	defaultValue: 'Rating',
																})}
															</Table.Cell>
															<Table.Cell basis={0.3}>
																{t('settings.ai-workbench.analytics.ratings-col-feedback', {
																	defaultValue: 'Written feedback',
																})}
															</Table.Cell>
														</Table.Header>
														{visibleFeedbackDetails.map((row, i) => (
															<Table.Row
																key={`${row.workspaceID}-${row.targetID}-${row.userID}-${row.interactionCreatedAt}-${i}`}
																tall
															>
																<Table.Cell basis={0.14}>
																	{moment(row.interactionCreatedAt).format('LLL')}
																</Table.Cell>
																<Table.Cell basis={0.18}>
																	{workspaceTitleById.get(row.workspaceID) ?? row.workspaceID}
																</Table.Cell>
																<Table.Cell basis={0.14}>
																	{labelForTargetType(row.targetType)}
																</Table.Cell>
																<Table.Cell basis={0.14}>{row.userID}</Table.Cell>
																<Table.Cell basis={0.1}>
																	{row.thumbsUp === true
																		? t('settings.ai-workbench.analytics.rating-thumb-up', {
																				defaultValue: 'Up',
																			})
																		: row.thumbsUp === false
																			? t('settings.ai-workbench.analytics.rating-thumb-down', {
																					defaultValue: 'Down',
																				})
																			: t('settings.ai-workbench.analytics.rating-thumb-none', {
																					defaultValue: '—',
																				})}
																</Table.Cell>
																<Table.Cell basis={0.3}>
																	<span
																		style={{
																			whiteSpace: 'pre-wrap',
																			wordBreak: 'break-word',
																		}}
																	>
																		{row.writtenFeedback?.trim() ? row.writtenFeedback : '—'}
																	</span>
																</Table.Cell>
															</Table.Row>
														))}
													</Table>
												</Box>
											)}
										</>
									)}
								</ChartCard>
							</Box>
						</Box>
					)}

					<Flex flexWrap="wrap" mx={-2}>
						<Box width={[1, 1, 1 / 2]} px={2}>
							<ChartCard>
								<ChartTitle>
									{t('settings.ai-workbench.analytics.chart-by-subject', {
										defaultValue: 'Volume by subject type',
									})}
								</ChartTitle>
								<ChartWrap>
									<Doughnut data={doughnutData} options={doughnutOptions} />
								</ChartWrap>
							</ChartCard>
						</Box>
						{showEvalRoleChart && (
							<Box width={[1, 1, 1 / 2]} px={2}>
								<ChartCard>
									<ChartTitle>
										{t('settings.ai-workbench.analytics.chart-eval-roles', {
											defaultValue: 'Conversations (Gesprek): employee vs manager context',
										})}
									</ChartTitle>
									<ChartWrap>
										<Bar data={evalRoleData} options={horizontalEvalOptions} />
									</ChartWrap>
								</ChartCard>
							</Box>
						)}
						<Box width={1} px={2}>
							<ChartCard>
								<ChartTitle>
									{stats.timeSeriesKind === 'month'
										? t('settings.ai-workbench.analytics.chart-over-time-monthly', {
												defaultValue: 'Monthly volume by subject type',
											})
										: stats.timeSeriesKind === 'day'
											? t('settings.ai-workbench.analytics.chart-over-time-daily', {
													defaultValue: 'Daily volume by subject type',
												})
											: t('settings.ai-workbench.analytics.chart-over-time-weekly', {
													defaultValue: 'Weekly volume by subject type',
												})}
								</ChartTitle>
								<TallChartWrap>
									<Bar data={stackedBarData} options={barOptions} />
								</TallChartWrap>
							</ChartCard>
						</Box>
						{showWorkspaceChart && (
							<Box width={1} px={2}>
								<ChartCard>
									<ChartTitle>
										{t('settings.ai-workbench.analytics.chart-workspaces', {
											defaultValue: 'Top workspaces by interactions',
										})}
									</ChartTitle>
									<TallChartWrap>
										<Bar data={workspaceBarData} options={workspaceBarOptions} />
									</TallChartWrap>
								</ChartCard>
							</Box>
						)}
					</Flex>

					{costs && (
						<Box mb={3}>
							<Heading3 mb={2}>{t('settings.ai-workbench.analytics.costs-title')}</Heading3>
							<Paragraph small mb={3} style={{ color: tokens.text.secondary }}>
								{t('settings.ai-workbench.analytics.costs-disclaimer')}
							</Paragraph>
							<Flex flexWrap="wrap" mx={-2}>
								<Box width={[1, 1 / 2]} px={2} mb={3}>
									<ChartCard>
										<ChartTitle>{t('settings.ai-workbench.analytics.costs-total')}</ChartTitle>
										<Paragraph style={{ fontSize: 28, fontWeight: 700 }}>
											{formatUsd(costs.totalCostUsd)}
										</Paragraph>
									</ChartCard>
								</Box>
								<Box width={[1, 1 / 2]} px={2} mb={3}>
									<ChartCard>
										<ChartTitle>{t('settings.ai-workbench.analytics.costs-per-prompt')}</ChartTitle>
										<Paragraph style={{ fontSize: 28, fontWeight: 700 }}>
											{costs.costPerPromptUsd == null ? '—' : formatUsd(costs.costPerPromptUsd)}
										</Paragraph>
										{costs.unpricedPromptCount > 0 && (
											<Paragraph small mb={0} style={{ color: tokens.text.secondary }}>
												{t('settings.ai-workbench.analytics.costs-unpriced', {
													count: costs.unpricedPromptCount,
												})}
											</Paragraph>
										)}
									</ChartCard>
								</Box>
							</Flex>
							{showWorkspaceCostChart && (
								<ChartCard>
									<ChartTitle>
										{t('settings.ai-workbench.analytics.costs-top-workspaces')}
									</ChartTitle>
									<TallChartWrap>
										<Bar data={workspaceCostBarData} options={workspaceCostBarOptions} />
									</TallChartWrap>
								</ChartCard>
							)}
						</Box>
					)}
				</>
			)}
		</Box>
	);
}
